import React from 'react';
import {
  SUPPRESSED_HINT,
  SUPPRESSED_LABEL,
  formatCount,
  formatPercent,
} from './format';

// The 17 UN Sustainable Development Goals with their official colours, so
// every goal shows as a tile even when no project addresses it yet.
const SDGS = [
  [1, 'No Poverty', '#E5243B'],
  [2, 'Zero Hunger', '#DDA63A'],
  [3, 'Good Health and Well-being', '#4C9F38'],
  [4, 'Quality Education', '#C5192D'],
  [5, 'Gender Equality', '#FF3A21'],
  [6, 'Clean Water and Sanitation', '#26BDE2'],
  [7, 'Affordable and Clean Energy', '#FCC30B'],
  [8, 'Decent Work and Economic Growth', '#A21942'],
  [9, 'Industry, Innovation and Infrastructure', '#FD6925'],
  [10, 'Reduced Inequalities', '#DD1367'],
  [11, 'Sustainable Cities and Communities', '#FD9D24'],
  [12, 'Responsible Consumption and Production', '#BF8B2E'],
  [13, 'Climate Action', '#3F7E44'],
  [14, 'Life Below Water', '#0A97D9'],
  [15, 'Life on Land', '#56C02B'],
  [16, 'Peace, Justice and Strong Institutions', '#00689D'],
  [17, 'Partnerships for the Goals', '#19486A'],
];

const MUTED = ['Other', 'Unclassified'];

function BarList({ rows, emptyText }) {
  if (!rows.length) return <p className="text-sm text-gray-500">{emptyText}</p>;
  const max = Math.max(...rows.map((row) => row.percent ?? 0), 1);
  return (
    <ul className="list-none p-0 m-0 space-y-2">
      {rows.map((row) => {
        const suppressed = row.learners === null;
        const muted = MUTED.includes(row.value);
        return (
          <li
            key={row.value}
            className="grid grid-cols-[minmax(0,11rem)_1fr_6.5rem] gap-3 items-center text-sm"
          >
            <span
              className={`truncate ${
                muted ? 'text-gray-500' : 'text-gray-800'
              }`}
              title={row.value}
            >
              {row.value}
            </span>
            <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
              {suppressed ? (
                <div className="h-full w-full rounded-full border border-dashed border-gray-300 bg-white" />
              ) : (
                <div
                  className={`h-full rounded-full ${
                    muted ? 'bg-gray-400' : 'bg-persian-500'
                  }`}
                  style={{
                    width: `${Math.max(((row.percent ?? 0) / max) * 100, 1)}%`,
                  }}
                />
              )}
            </div>
            <span
              className="text-right text-xs tabular-nums text-gray-700"
              title={suppressed ? SUPPRESSED_HINT : undefined}
            >
              {suppressed ? (
                <span className="text-gray-500">{SUPPRESSED_LABEL}</span>
              ) : (
                <>
                  <span className="font-semibold">
                    {formatCount(row.learners)}
                  </span>{' '}
                  <span className="text-gray-400">
                    ({formatPercent(row.percent)})
                  </span>
                </>
              )}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function ProjectsCard({ projects, action }) {
  const withData = projects?.cohorts_with_data ?? 0;
  const sdgByGoal = new Map(
    (projects?.sdgs ?? []).map((sdg) => [sdg.goal, sdg])
  );

  return (
    <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-gray-900">
            Graduates&apos; projects
          </h2>
          <p className="text-xs text-gray-500 mt-1">
            {withData > 0
              ? `Based on ${withData} cohort${
                  withData === 1 ? "'s" : "s'"
                } graduation lists. One learner can have projects in several sectors or goals, so shares can add up to more than 100%.`
              : 'Sector, SDG and partner data comes from imported graduation lists.'}
          </p>
        </div>
        {withData > 0 && action}
      </div>

      {withData === 0 ? (
        <p className="text-sm text-gray-500">Not yet available.</p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 max-w-md">
            <div>
              <p className="text-xs uppercase tracking-wide text-gray-500">
                Learners with a project
              </p>
              <p className="text-2xl font-semibold tabular-nums text-gray-900">
                {formatCount(projects.learners)}
              </p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-gray-500">
                Projects
              </p>
              <p className="text-2xl font-semibold tabular-nums text-gray-900">
                {formatCount(projects.projects)}
              </p>
            </div>
          </div>

          <div>
            <p className="text-sm font-semibold text-gray-900 mb-3">
              By sector
            </p>
            <BarList
              rows={projects.sectors ?? []}
              emptyText="No sectors recorded."
            />
          </div>

          <div>
            <p className="text-sm font-semibold text-gray-900 mb-3">
              UN Sustainable Development Goals
            </p>
            {sdgByGoal.size === 0 ? (
              <p className="text-sm text-gray-500">
                No projects have been mapped to the SDGs yet.
              </p>
            ) : (
              <>
                <div className="grid grid-cols-3 md:grid-cols-6 xl:grid-cols-9 gap-2">
                  {SDGS.map(([goal, name, colour]) => {
                    const sdg = sdgByGoal.get(goal);
                    let detail = 'no projects yet';
                    if (sdg && sdg.learners === null) detail = SUPPRESSED_HINT;
                    else if (sdg) {
                      detail = `${formatCount(
                        sdg.learners
                      )} learners, ${formatCount(sdg.projects)} projects`;
                    }
                    return (
                      <div
                        key={goal}
                        title={`${goal}. ${sdg?.value || name} · ${detail}`}
                        className="rounded-lg p-2 min-h-[5.5rem] flex flex-col justify-between"
                        style={{
                          backgroundColor: sdg ? colour : '#F3F4F6',
                          color: sdg ? '#FFFFFF' : '#9CA3AF',
                        }}
                      >
                        <span className="text-[10px] leading-tight font-medium">
                          {goal}. {sdg?.value || name}
                        </span>
                        <span className="text-base font-semibold tabular-nums">
                          {sdg ? formatCount(sdg.learners) : '–'}
                        </span>
                      </div>
                    );
                  })}
                </div>
                <p className="text-[11px] text-gray-500 mt-2">
                  Learners whose projects address each goal.
                </p>
              </>
            )}
          </div>

          <div>
            <p className="text-sm font-semibold text-gray-900 mb-3">
              Partners and programmes
            </p>
            <BarList
              rows={projects.partners ?? []}
              emptyText="No partners recorded."
            />
            {projects.learners_without_partner > 0 && (
              <p className="text-[11px] text-gray-500 mt-2">
                {formatCount(projects.learners_without_partner)} learners&apos;
                projects name no partner.
              </p>
            )}
          </div>
        </>
      )}
    </section>
  );
}

export default ProjectsCard;
